import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Connector } from 'wagmi';
import { useAccount, useBalance, useConnect, useDisconnect, useEnsName } from 'wagmi';
import { Button, CopyButton, Modal, Popover, Spinner, useToast } from '@/components/ui';
import { cn } from '@/lib/cn';
import { ethFromWei, explorerUrl, hueFromAddress, shortAddress } from '@/lib/format';
import { useMarket } from '@/providers/market';
import { useConnectModal } from './AppShell';

/**
 * `Popover` owns its open state and only closes on a mousedown outside its root,
 * so anything selected inside the panel has to emit one from the document.
 */
function dismissPopover() {
  document.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
}

/**
 * The wagmi config can surface two connectors with the same display name (an
 * injected provider plus its dedicated connector). Two identical rows read as a
 * bug, so collapse by name and keep the injected one.
 */
function dedupeConnectors(connectors: readonly Connector[]): Connector[] {
  const byName = new Map<string, Connector>();
  for (const connector of connectors) {
    const key = connectorName(connector).toLowerCase();
    const existing = byName.get(key);
    if (!existing || (connector.type === 'injected' && existing.type !== 'injected')) {
      byName.set(key, connector);
    }
  }
  return [...byName.values()];
}

/**
 * `injected()` reports the bare word "Injected" when no extension announced
 * itself. That's a library internal, not something a visitor recognises — with a
 * wallet installed the same row would read "MetaMask".
 */
function connectorName(connector: Connector): string {
  const name = connector.name.trim();
  return /^injected$/i.test(name) ? 'Browser wallet' : name;
}

/** The hint earns its line only when it says something the name didn't. */
function connectorHint(connector: Connector, name: string): string | null {
  const hint = connector.type === 'injected' ? 'Detected' : 'Popular';
  return hint.toLowerCase() === name.toLowerCase() ? null : hint;
}

/** Deterministic address mark — no avatar service, no network round-trip. */
function AddressMark({ address, size = 20 }: { address: string; size?: number }) {
  const hue = hueFromAddress(address);
  return (
    <span
      aria-hidden="true"
      className="shrink-0 rounded-md ring-1 ring-line"
      style={{
        width: size,
        height: size,
        backgroundImage: `conic-gradient(from ${hue}deg, hsl(${hue} 80% 58%), hsl(${(hue + 110) % 360} 74% 50%), hsl(${(hue + 220) % 360} 78% 60%), hsl(${hue} 80% 58%))`,
      }}
    />
  );
}

export function WalletButton() {
  const { address, isConnected, chainId } = useAccount();
  const { connect, connectors } = useConnect();
  const { disconnect } = useDisconnect();
  const { data: balance } = useBalance({ address });
  const { data: ensName } = useEnsName({ address });
  const { isDemo } = useMarket();
  const { push } = useToast();

  // Open state lives on the shell so any component — a trade panel's primary
  // CTA, an empty portfolio — can start the connect flow. The modal itself is
  // still rendered here, once, next to the trigger it belongs to.
  const { open, setOpen, close } = useConnectModal();
  const [pendingId, setPendingId] = useState<string | null>(null);

  // A connect prompt has nothing to say once a wallet is attached, and callers
  // elsewhere can't know that — so the shell-level flag folds itself away.
  useEffect(() => {
    if (isConnected && open) close();
  }, [isConnected, open, close]);

  const options = useMemo(() => dedupeConnectors(connectors), [connectors]);
  const balanceLabel = balance ? ethFromWei(balance.value, { digits: 4 }) : '—';

  /* ── Disconnected ───────────────────────────────────────────────────── */
  if (!isConnected || !address) {
    const onConnect = (connector: Connector) => {
      setPendingId(connector.uid);
      connect(
        { connector },
        {
          onError: (error) =>
            push({ tone: 'error', title: 'Wallet connection failed', description: error.message }),
          onSuccess: () => close(),
          onSettled: () => setPendingId(null),
        },
      );
    };

    return (
      <>
        <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
          {/* "Connect wallet" only from lg. Between md and lg the desktop nav is
              already on while every control is still full width, and the header
              row overflows a 768px viewport (portrait iPad) by 27px. */}
          <span className="hidden lg:inline">Connect wallet</span>
          <span className="lg:hidden">Connect</span>
        </Button>

        <Modal
          open={open}
          onClose={close}
          title="Connect a wallet"
          description="Trade on the curve, launch a token, or redeem at floor."
          size="sm"
          footer={
            <p className="text-2xs text-subtle">
              Connecting is non-custodial: bull.markets never holds your keys and never moves funds
              without a transaction you sign.
            </p>
          }
        >
          <ul role="list" className="space-y-2">
            {options.map((connector) => {
              const pending = pendingId === connector.uid;
              const name = connectorName(connector);
              const hint = connectorHint(connector, name);
              return (
                <li key={connector.uid}>
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => onConnect(connector)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg border border-line bg-elevated px-3 py-3 text-left transition-colors duration-200 ease-swift',
                      'hover:border-line-strong hover:bg-overlay disabled:opacity-60',
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-bull-sheen font-display text-sm font-extrabold text-[rgb(var(--on-bull))]"
                    >
                      {name.charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-fg">{name}</span>
                      {hint ? <span className="block text-2xs text-subtle">{hint}</span> : null}
                    </span>
                    {pending ? <Spinner size={16} /> : null}
                  </button>
                </li>
              );
            })}

            {isDemo && (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    close();
                    push({
                      tone: 'info',
                      title: 'Previewing the demo wallet',
                      description:
                        'Nothing is connected. Balances and positions on screen are synthesised for design preview.',
                    });
                  }}
                  className="flex w-full items-center gap-3 rounded-lg border border-dashed border-line px-3 py-3 text-left transition-colors duration-200 ease-swift hover:border-warn/60 hover:bg-elevated"
                >
                  <span
                    aria-hidden="true"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-line bg-elevated font-display text-sm font-extrabold text-warn"
                  >
                    D
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-fg">
                      Preview as demo wallet
                    </span>
                    <span className="block text-2xs text-warn">
                      Design preview only — no signer, no funds
                    </span>
                  </span>
                </button>
              </li>
            )}
          </ul>
        </Modal>
      </>
    );
  }

  /* ── Connected ──────────────────────────────────────────────────────── */
  const label = ensName ?? shortAddress(address);
  // Below sm the row has ~6px to spare, so the trigger drops to a shorter
  // elision there rather than being the thing that scrolls the page sideways.
  const tightLabel = ensName ?? shortAddress(address, 3);

  return (
    <Popover
      align="end"
      className="w-72"
      trigger={
        /* Popover wraps `trigger` in its own <button>, so this must not be one. */
        <span className="flex h-9 items-center gap-2 rounded-md border border-line bg-elevated pl-1.5 pr-2 text-sm transition-colors duration-200 ease-swift hover:border-line-strong">
          <AddressMark address={address} />
          <span aria-hidden="true" className="max-w-[6rem] truncate font-medium text-fg sm:hidden">
            {tightLabel}
          </span>
          <span aria-hidden="true" className="hidden max-w-[8.5rem] truncate font-medium text-fg sm:inline">
            {label}
          </span>
          <span aria-hidden="true" className="hidden font-mono text-2xs text-muted lg:inline">
            {balanceLabel}
          </span>
          <span className="sr-only">{`Wallet ${label}, balance ${balanceLabel}. Open wallet menu`}</span>
        </span>
      }
    >
      <div>
        <div className="flex items-center gap-2 px-2 pb-2 pt-1">
          <AddressMark address={address} size={28} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-fg">{label}</p>
            <p className="truncate font-mono text-2xs text-subtle">{shortAddress(address, 6)}</p>
          </div>
          <CopyButton value={address} label="Copy address" />
        </div>

        <div className="rule my-1" />

        <dl className="flex items-center justify-between px-2 py-1.5">
          <dt className="text-2xs uppercase tracking-[0.14em] text-subtle">Balance</dt>
          <dd className="font-mono text-sm text-fg">{balanceLabel}</dd>
        </dl>

        <div className="rule my-1" />

        <ul role="list" className="space-y-0.5 pt-0.5">
          <li>
            <a
              href={explorerUrl(chainId ?? 84532, 'address', address)}
              target="_blank"
              rel="noreferrer noopener"
              onClick={dismissPopover}
              className="flex items-center justify-between rounded-lg px-2 py-2 text-sm text-muted transition-colors duration-200 ease-swift hover:bg-elevated hover:text-fg"
            >
              View on explorer <span aria-hidden="true">↗</span>
            </a>
          </li>
          <li>
            <Link
              to="/portfolio"
              onClick={dismissPopover}
              className="flex items-center justify-between rounded-lg px-2 py-2 text-sm text-muted transition-colors duration-200 ease-swift hover:bg-elevated hover:text-fg"
            >
              Portfolio <span aria-hidden="true">→</span>
            </Link>
          </li>
          <li>
            <button
              type="button"
              onClick={() => {
                dismissPopover();
                disconnect();
                push({ tone: 'info', title: 'Wallet disconnected' });
              }}
              className="w-full rounded-lg px-2 py-2 text-left text-sm text-down transition-colors duration-200 ease-swift hover:bg-elevated"
            >
              Disconnect
            </button>
          </li>
        </ul>
      </div>
    </Popover>
  );
}

export default WalletButton;
